/**
 * Reine Stundenplan-Logik: Wochenbuchstabe, Gueltigkeit, Stunden eines Tages.
 *
 * ⚠️ Alles DATUMSBASIERT (siehe datum.ts) - ein Stundenplan kennt keine
 * Zeitzone. "Mo 8:00" ist Ortszeit der Schule, und die Frage "welche Woche ist
 * heute" ist reine Kalenderarithmetik.
 */
import { alsTagzahl, wochenStart, wochentag, type Datum } from '../recurrence/datum'

export type Wochenbuchstabe = 'A' | 'B'
export type WeekPattern = 'single' | 'ab'

export interface PlanKopf {
  validFrom: Datum
  validUntil: Datum | null
  weekPattern: WeekPattern
  abAnchorMonday: Datum | null
}

export interface Stunde {
  /** ISO: 1 = Montag … 7 = Sonntag */
  weekday: number
  /** NULL = jede Woche; 'A' | 'B' nur im A/B-Plan */
  week: Wochenbuchstabe | null
  /** "HH:MM" */
  startsAt: string
  endsAt: string
}

/**
 * A oder B fuer das Datum, abgeleitet aus dem Anker-Montag einer A-Woche.
 *
 * Gerade Wochendistanz = A, ungerade = B - in beide Richtungen. Ferien
 * verschieben nichts: Schulen zaehlen durch. Der Sonntag gehoert zur Woche
 * seines Montags, deshalb wird ZUERST auf den Montag gerundet und dann gezaehlt.
 */
export function wochenBuchstabe(datum: Datum, ankerMontag: Datum): Wochenbuchstabe {
  const wochen = (alsTagzahl(wochenStart(datum)) - alsTagzahl(wochenStart(ankerMontag))) / 7
  // -2 % 2 ist -0 und -0 === 0 - die Paritaet gilt auch rueckwaerts.
  return wochen % 2 === 0 ? 'A' : 'B'
}

/** Gilt der Plan am Datum? `valid_until` ist EINSCHLIESSLICH (der letzte Schultag). */
export function gueltigAm(plan: PlanKopf, datum: Datum): boolean {
  return datum >= plan.validFrom && (plan.validUntil === null || datum <= plan.validUntil)
}

/** Die Woche des Plans am Datum; NULL im single-Plan. Ein A/B-Plan ohne Anker ist ein Datenfehler. */
export function wocheDesPlans(plan: PlanKopf, datum: Datum): Wochenbuchstabe | null {
  if (plan.weekPattern !== 'ab') return null
  if (plan.abAnchorMonday === null) throw new Error('A/B-Plan ohne Anker-Montag')
  return wochenBuchstabe(datum, plan.abAnchorMonday)
}

/**
 * Die Stunden eines Tages, nach Beginn sortiert. Ausserhalb der Gueltigkeit
 * nichts; im A/B-Plan nur die Stunden der aktuellen Woche plus die ohne
 * Buchstabe (die gelten jede Woche). Im single-Plan wird nicht gefiltert.
 */
export function stundenAmTag<T extends Stunde>(plan: PlanKopf, stunden: T[], datum: Datum): T[] {
  if (!gueltigAm(plan, datum)) return []
  const tag = wochentag(datum)
  const woche = wocheDesPlans(plan, datum)
  return stunden
    .filter((s) => s.weekday === tag && (woche === null || s.week === null || s.week === woche))
    .sort((a, b) => (a.startsAt < b.startsAt ? -1 : a.startsAt > b.startsAt ? 1 : 0))
}
